import React, { useState, useEffect } from "react";
import { Box, Grid } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import DatadogAlertsTable from "../../components/dataDog/dataDogAlertsTable";
import GenericTable from "../../components/shared/genericTable/genericTable";
import { callAPI } from "../../functions/api";
import { getCountryCodeFromRegion, regionToCountryCode } from "../../functions/regions";
import { MonitoringViewSideBar } from "./monitoringView.sidebar";

dayjs.extend(relativeTime);

const MonitoringView = () => {
  const [alertData, setAlertData] = useState([]);
  const [hostData, setHostData] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loadingDataDogHosts, setLoadingDataDogHosts] = useState(true);
  const [loadingDatadogAlerts, setLoadingDatadogAlerts] = useState(true);
  useEffect(() => {
    const fetchAll = async () => {
      try {
        callAPI("logging/datadog/hosts", "GET", null, true)
          .then(setHostData)
          .catch((error) => {
            devError("Error fetching active tickets", error);
          })
          .finally(() => {
            setLoadingDataDogHosts(false);
          });

        callAPI("logging/datadog/alerts", "GET", null, true)
          .then(setAlertData)
          .catch((error) => {
            devError("Error fetching active tickets", error);
          })
          .finally(() => {
            setLoadingDatadogAlerts(false);
          });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchAll();
  }, []);

  const renderUtilBar = (value = 0) => {
    const percent = Math.round(value);
    const fillWidth = Math.max(percent, 5); // Ensures visibility even at 0%

    const color =
      percent > 80
        ? "#e53935" // red
        : percent > 60
          ? "#fb8c00" // orange
          : percent > 40
            ? "#fdd835" // yellow
            : "#43a047"; // green

    return (
      <div
        title={`${percent}%`}
        style={{
          width: "100%",
          backgroundColor: "#f0f0f0",
          borderRadius: 4,
          height: 20,
          position: "relative",
          overflow: "hidden",
          fontSize: 12,
          fontWeight: "bold",
          color: percent > 50 ? "white" : "#333",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: `${fillWidth}%`,
            backgroundColor: percent > 0 ? color : "#ccc",
            height: "100%",
            borderRadius: 4,
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 0,
          }}
        />
        <span style={{ zIndex: 1 }}>{`${percent}%`}</span>
      </div>
    );
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 80px)",
        backgroundColor: "background.default",
      }}
    >
      <Grid
        container
        sx={{
          height: "100%",
          paddingLeft: "20px",
          paddingRight: "20px",
          paddingTop: "40px",
          overflow: "hidden",
          flexDirection: "row",
        }}
        gap={2}
      >
        <Grid container sx={{}} height={"100%"} gap={2}>
          <Grid container sx={{ height: "50%" }} columnSpacing={2} rowSpacing={2}>
            <Grid item xs={12} sx={{ height: "100%" }}>
              <DatadogAlertsTable data={alertData} updatedAt={updatedAt} />
            </Grid>
          </Grid>
          <Grid
            container
            sx={{ height: "50%", paddingTop: "20px" }}
            columnSpacing={2}
            rowSpacing={2}
          >
            <Grid item xs={12} sx={{ height: "100%" }}>
              <GenericTable
                title="DataDog Hosts"
                showSearch={true}
                showActionColumn={true}
                maxColumnWidths={{
                  "Aws ID": "220px",
                }}
                customColumns={[
                  {
                    name: "Host",
                    align: "left",
                    score: (row) => row.name?.toLowerCase() ?? "",
                    render: (row) => {
                      const displayName =
                        row.name && row.name.length > 24 ? row.name.slice(0, 24) + "..." : row.name;

                      return (
                        <span
                          style={{
                            fontWeight: "bold",
                            textTransform: "uppercase",
                          }}
                          title={row.name}
                        >
                          {displayName}
                        </span>
                      );
                    },
                  },
                  {
                    name: "Aws ID",
                    align: "left",
                    score: (row) => row.awsId?.toLowerCase() ?? "",
                    render: (row) => {
                      return (
                        <span
                          style={{
                            fontWeight: "bold",
                            textTransform: "uppercase",
                          }}
                          title={row.awsId}
                        >
                          {row.awsId}
                        </span>
                      );
                    },
                  },
                  {
                    name: "Status",
                    align: "left",
                    score: (row) => {
                      const order = {
                        up: 1,
                        muted: 2,
                        down: 3,
                        unknown: 4,
                      };
                      const status = row.status?.toLowerCase() || "unknown";
                      return order[status] ?? 99;
                    },
                    render: (row) => {
                      const status = row.status?.toLowerCase() || "unknown";
                      const colorMap = {
                        up: "#70C930",
                        down: "#BA1650",
                        muted: "#F9CA00",
                        unknown: "#999999",
                      };
                      const backgroundColor = colorMap[status] || "#ccc";

                      return (
                        <span
                          style={{
                            display: "inline-block",
                            padding: "4px 10px",
                            borderRadius: "999px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            fontSize: "12px",
                            color: "#fff",
                            backgroundColor,
                            minWidth: "60px",
                            textAlign: "center",
                          }}
                          title={row.status}
                        >
                          {row.status}
                        </span>
                      );
                    },
                  },
                  {
                    name: "CPU",
                    score: (row) => row.cpuUtilization ?? 0,
                    align: "center",
                    render: (row) => renderUtilBar(row.cpuUtilization ?? 0),
                  },
                  {
                    name: "Memory",
                    score: (row) => row.memoryUtilization ?? 0,
                    align: "center",
                    render: (row) => renderUtilBar(row.memoryUtilization ?? 0),
                  },
                  {
                    name: "Disk",
                    score: (row) => row.diskUtilization ?? 0,
                    align: "center",
                    render: (row) => renderUtilBar(row.diskUtilization ?? 0),
                  },
                  {
                    name: "Application",
                    align: "center",
                    score: (row) => row.application?.toLowerCase() ?? "",
                    render: (row) => (
                      <span
                        style={{
                          fontWeight: "bold",
                          textTransform: "uppercase",
                        }}
                        title={row.application}
                      >
                        {row.application}
                      </span>
                    ),
                  },

                  {
                    name: "Zone",
                    align: "center",
                    score: (row) => row.regionDisplayName || "",
                    render: (row) => {
                      const regionName = row.availabilityZone || "Unknown";
                      const countryCode = getCountryCodeFromRegion(regionName);
                      let flagSrc = "";
                      try {
                        flagSrc = require(`../../assets/flags/${countryCode?.toLowerCase()}.png`);
                      } catch (err) {
                        flagSrc = null;
                      }

                      return (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            justifyContent: "center",
                          }}
                          title={regionName}
                        >
                          {flagSrc && (
                            <img
                              src={flagSrc}
                              alt={countryCode?.toUpperCase()}
                              width="24"
                              height="18"
                              style={{ borderRadius: "2px", objectFit: "cover" }}
                            />
                          )}
                        </span>
                      );
                    },
                  },
                  {
                    name: "AWS Account",
                    align: "left",
                    score: (row) => row.awsAccountName?.toLowerCase() ?? "",
                    render: (row) => (
                      <span
                        style={{
                          fontWeight: "bold",
                          textTransform: "uppercase",
                        }}
                        title={row.awsAccountName}
                      >
                        {row.awsAccountName}
                      </span>
                    ),
                  },
                  {
                    name: "Environment",
                    align: "center",
                    score: (row) => row.environment?.toLowerCase() ?? "",
                    render: (row) => (
                      <span
                        style={{
                          fontWeight: "bold",
                          textTransform: "uppercase",
                        }}
                        title={row.environment}
                      >
                        {row.environment}
                      </span>
                    ),
                  },

                  {
                    name: "Platform",
                    align: "center",
                    score: (row) => row.platform?.toLowerCase() ?? "",
                    render: (row) => (
                      <span
                        style={{
                          fontWeight: "bold",
                          textTransform: "uppercase",
                        }}
                        title={row.platform}
                      >
                        {row.platform}
                      </span>
                    ),
                  },
                  {
                    name: "Owner",
                    align: "left",
                    score: (row) => row.serviceOwner?.toLowerCase() ?? "",
                    render: (row) => (
                      <span
                        style={{
                          fontWeight: "bold",
                          textTransform: "uppercase",
                        }}
                        title={row.serviceOwner}
                      >
                        {row.serviceOwner}
                      </span>
                    ),
                  },
                ]}
                loading={loadingDataDogHosts}
                data={hostData}
              ></GenericTable>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <MonitoringViewSideBar />
    </Box>
  );
};

export default MonitoringView;
